import React, { FunctionComponent, ReactElement } from 'react';
import classNames from 'classnames';

import { ReactComponent as ScholasticLogoOutline } from '../../assets/logos/scholastic-logo-white-outline.svg';

/**
 * Generic Link Properties
 */
export interface LinkProps {
  /**
   * Additional styles to be applied to link
   */
  className?: string;

  /**
   * Destination of link
   */
  url?: string;

  /**
   * Choose whether to open link in same tab or new one
   *
   * @default '_blank'
   */
  target?: '_self' | '_blank';
}

/**
 * Branding information to show in the footer
 */
export interface PageFooterBrand extends LinkProps {
  /**
   * SVG icon to display
   *
   * @defaultValue ScholasticLogoOutline
   */
  logo?: FunctionComponent;

  /**
   * Alternate text for icon
   *
   * @defaultValue 'about Scholastic'
   */
  label?: string;
}

/**
 * A link displayed in the Page Footer
 */
export interface PageFooterLink extends LinkProps {
  /**
   * Display content of the link
   */
  content: ReactElement | string;

  /**
   * Additional styles to be applied to link
   */
  className?: string;
}

/**
 * Attributes and Properties for the PageFooter component
 */
export interface PageFooterProps {
  /**
   * Brand logo and link, with alternate text.
   *
   * @defaultValue the Scholastic outline logo and about page
   */
  brand?: PageFooterBrand;

  /**
   * Additional links
   *
   * @defaultValue Scholastic Privacy Policy and Terms of Use
   */
  links?: PageFooterLink[];

  /**
   * Date which represents what copyright year to show
   *
   * @defaultValue current date
   */
  copyrightYear?: Date;

  /**
   * Additional class name to apply to overall page footer
   */
  className?: string;
}

/**
 * The PageFooter is a standard footer for Scholastic applications.
 * Including links with more information on the Scholastic organization,
 * on the Privacy Policy, and on the Terms of Use.
 */
export const PageFooter: FunctionComponent<PageFooterProps> = ({
  brand: {
    className: brandClassName,
    logo: BrandLogo = ScholasticLogoOutline,
    label: brandLabel = 'about Scholastic',
    url: brandUrl = 'https://www.scholastic.com/aboutscholastic',
    target: brandTarget = '_blank',
  } = {},
  links = [
    {
      content: 'Privacy Policy',
      url: 'https://www.scholastic.com/edtechprivacy.htm',
      className: 'vol-page-footer__nav-link--privacy',
    },
    {
      content: 'Terms of Use',
      url: 'https://www.scholastic.com/terms.htm',
      className: 'vol-page-footer__nav-link--terms',
    },
  ],
  copyrightYear = new Date(),
  className = '',
}: PageFooterProps): ReactElement => (
  <nav className={classNames('vol-page-footer', className)}>
    <a
      className={classNames('vol-page-footer__brand', brandClassName)}
      href={brandUrl}
      target={brandTarget}
      rel={brandTarget === '_blank' ? 'noopener noreferrer' : undefined}
    >
      <BrandLogo aria-label={brandLabel} />
    </a>

    <ul className="vol-page-footer__nav">
      {links.map(({ content, className: linkClassName, url, target = '_blank' }) => (
        <li key={url} className="vol-page-footer__nav-item">
          <a
            className={classNames('vol-page-footer__nav-link', linkClassName)}
            href={url}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
          >
            {content}
          </a>
        </li>
      ))}
    </ul>

    <span className="vol-page-footer__text">
      TM ® &amp; © {copyrightYear.getFullYear()} Scholastic Inc. All Rights Reserved.
    </span>
  </nav>
);

export default PageFooter;
